import axios from 'axios'
import OSS from 'ali-oss'

const OSS_STS_API = 'common/getOssSts'

function padNumber(value) {
  return String(value).padStart(2, '0')
}

function buildDateDirectory(targetDirectory = 'user/') {
  const date = new Date()
  const year = date.getFullYear()
  const month = padNumber(date.getMonth() + 1)
  const day = padNumber(date.getDate())
  const normalizedBase = targetDirectory.replace(/^\/+|\/+$/g, '')
  return normalizedBase ? `${normalizedBase}/${year}${month}${day}/` : `${year}${month}${day}/`
}

function buildFileName(file) {
  const originalName = file && file.name ? String(file.name) : ''
  const safeName = originalName
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-zA-Z0-9_-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  const extensionFromName = originalName.includes('.') ? originalName.split('.').pop() : ''
  const extensionFromType = file && file.type ? String(file.type).split('/').pop() : ''
  const extension = extensionFromName || extensionFromType || 'webm'
  const suffix = safeName ? `-${safeName}` : ''

  return `${Date.now()}${suffix}.${extension}`
}

function normalizePublicBaseUrl(data) {
  const candidates = [data.cdnurl, data.publicUrlBase, data.host, data.domain]
  const matched = candidates.find(item => item && typeof item === 'string')
  return matched ? matched.replace(/\/$/, '') : ''
}

function pickFirstValue(candidates) {
  return candidates.find(item => item !== undefined && item !== null && item !== '')
}

function normalizeStsPayload(payload) {
  const root = payload || {}
  const nested = root.data || {}
  const defaults = root.defaults_used || nested.defaults_used || {}
  const credentials = root.credentials || nested.credentials || root.Credentials || nested.Credentials || {}

  return {
    region: pickFirstValue([root.region, nested.region, defaults.region, root.Region, nested.Region]),
    bucket: pickFirstValue([root.bucket, nested.bucket, defaults.bucket, root.Bucket, nested.Bucket]),
    accessKeyId: pickFirstValue([
      root.accessKeyId,
      root.accessKey,
      root.ak,
      root.accessid,
      nested.accessKeyId,
      nested.accessKey,
      nested.ak,
      nested.accessid,
      defaults.accessKeyId,
      defaults.accessid,
      credentials.accessKeyId,
      credentials.accessKey,
      credentials.ak,
      root.AccessKeyId,
      nested.AccessKeyId,
      credentials.AccessKeyId
    ]),
    accessId: pickFirstValue([
      root.accessid,
      nested.accessid,
      root.accessId,
      nested.accessId,
      root.OSSAccessKeyId,
      nested.OSSAccessKeyId
    ]),
    accessKeySecret: pickFirstValue([
      root.accessKeySecret,
      root.accessSecret,
      root.sk,
      nested.accessKeySecret,
      nested.accessSecret,
      nested.sk,
      defaults.accessKeySecret,
      defaults.accessSecret,
      defaults.sk,
      credentials.accessKeySecret,
      credentials.accessSecret,
      credentials.sk,
      root.AccessKeySecret,
      nested.AccessKeySecret,
      credentials.AccessKeySecret
    ]),
    stsToken: pickFirstValue([
      root.stsToken,
      root.token,
      nested.stsToken,
      nested.token,
      credentials.stsToken,
      credentials.token,
      root.securityToken,
      nested.securityToken,
      defaults.securityToken,
      credentials.securityToken,
      root.SecurityToken,
      nested.SecurityToken,
      credentials.SecurityToken
    ]),
    endpoint: pickFirstValue([root.endpoint, nested.endpoint, defaults.endpoint, root.Endpoint, nested.Endpoint]),
    cdnurl: pickFirstValue([root.cdnurl, nested.cdnurl, defaults.cdnurl, root.cdnUrl, nested.cdnUrl]),
    publicUrlBase: pickFirstValue([root.publicUrlBase, nested.publicUrlBase]),
    host: pickFirstValue([root.host, nested.host, root.Host, nested.Host]),
    domain: pickFirstValue([root.domain, nested.domain, root.Domain, nested.Domain]),
    dirPrefix: pickFirstValue([root.dirPrefix, nested.dirPrefix, defaults.dir, root.dir, nested.dir, root.prefix, nested.prefix]),
    cname: pickFirstValue([root.cname, nested.cname]),
    secure: pickFirstValue([root.secure, nested.secure]),
    authorizationV4: pickFirstValue([root.authorizationV4, nested.authorizationV4]),
    refreshSTSToken: pickFirstValue([root.refreshSTSToken, nested.refreshSTSToken]),
    refreshSTSTokenInterval: pickFirstValue([root.refreshSTSTokenInterval, nested.refreshSTSTokenInterval])
  }
}

function buildClientConfig(data) {
  const config = {
    region: data.region,
    bucket: data.bucket,
    accessKeyId: data.accessKeyId,
    accessKeySecret: data.accessKeySecret,
    endpoint: data.endpoint,
    cname: Boolean(data.cname),
    secure: data.secure !== false,
    authorizationV4: data.authorizationV4 !== false,
    refreshSTSToken: data.refreshSTSToken
      ? async () => {
          const refreshed = await getOssStsConfig()
          return {
            accessKeyId: refreshed.accessKeyId,
            accessKeySecret: refreshed.accessKeySecret,
            stsToken: refreshed.stsToken
          }
        }
      : undefined,
    refreshSTSTokenInterval: data.refreshSTSTokenInterval || 300000
  }

  if (data.stsToken) {
    config.stsToken = data.stsToken
  }

  return config
}

function validateStsConfig(data) {
  const requiredFields = ['region', 'bucket', 'accessKeyId', 'accessKeySecret']
  const missingFields = requiredFields.filter(field => !data || !data[field])
  if (missingFields.length) {
    throw new Error(`Missing OSS STS config fields: ${missingFields.join(', ')}`)
  }
}

export async function getOssStsConfig() {
  const response = await axios.post(OSS_STS_API)
  const payload = response && response.data ? response.data : response
  const normalizedPayload = normalizeStsPayload(payload)
  validateStsConfig(normalizedPayload)
  return normalizedPayload
}

export default async function uploadFile(file, targetDirectory = 'user/') {
  if (!file) {
    throw new Error('No file provided for OSS upload')
  }

  const stsConfig = await getOssStsConfig()
  const uploadDirectory = targetDirectory || stsConfig.dirPrefix || ''
  const directory = buildDateDirectory(uploadDirectory)
  const uploadedName = buildFileName(file)
  const objectKey = `${directory}${uploadedName}`
  const client = new OSS(buildClientConfig(stsConfig))
  await client.put(objectKey, file)

  const publicBaseUrl = normalizePublicBaseUrl(stsConfig)

  return {
    originalName: file.name || uploadedName,
    uploadedName,
    url: `/${objectKey}`,
    fullurl: publicBaseUrl ? `${publicBaseUrl}/${objectKey}` : `https://${stsConfig.bucket}.${stsConfig.region}.aliyuncs.com/${objectKey}`
  }
}
